import { useCallback, useEffect, useState } from "react";

export const GHANA_CITIES = [
  { name: "Accra", lat: 5.6037, lng: -0.187 },
  { name: "Kumasi", lat: 6.6885, lng: -1.6244 },
  { name: "Tamale", lat: 9.4008, lng: -0.8393 },
  { name: "Takoradi", lat: 4.8845, lng: -1.7554 },
  { name: "Cape Coast", lat: 5.1053, lng: -1.2466 },
  { name: "Tema", lat: 5.6698, lng: -0.0166 },
  { name: "Ho", lat: 6.6008, lng: 0.4713 },
  { name: "Koforidua", lat: 6.0941, lng: -0.2591 },
  { name: "Sunyani", lat: 7.3349, lng: -2.3123 },
  { name: "Wa", lat: 10.0601, lng: -2.5099 },
  { name: "Bolgatanga", lat: 10.7856, lng: -0.8514 },
  { name: "Techiman", lat: 7.5905, lng: -1.9395 },
  { name: "Obuasi", lat: 6.2021, lng: -1.6641 },
  { name: "Winneba", lat: 5.3511, lng: -0.6231 },
  { name: "Hohoe", lat: 7.1519, lng: 0.4738 },
];

export function nearestCity(lat, lng) {
  let best = null, bestD = Infinity;
  for (const c of GHANA_CITIES) {
    const d = (c.lat - lat) ** 2 + (c.lng - lng) ** 2;
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

const KEY = "icaard_geo_v1";
const GPS_TTL_MS = 15 * 60 * 1000;

function readStored() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    if (!s) return null;
    if (s.source === "manual") return s;
    if (Date.now() - (s.ts || 0) < GPS_TTL_MS) return s;
  } catch (_) {}
  return null;
}

/**
 * Shared location hook — auto-detects browser GPS (asks once), falls back to a
 * manual city picker. Result persisted so every search page shares one answer.
 *   geo = { lat, lng, city, source: "gps" | "manual" } | null
 */
export default function useGeo({ auto = true } = {}) {
  const [geo, setGeo] = useState(readStored);
  const [status, setStatus] = useState(() => (readStored()?.source || "idle"));

  const detect = useCallback(() => {
    if (!navigator.geolocation) { setStatus("denied"); return; }
    setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const g = { lat, lng, city: nearestCity(lat, lng)?.name || "", source: "gps", ts: Date.now() };
        localStorage.setItem(KEY, JSON.stringify(g));
        setGeo(g); setStatus("gps");
      },
      () => setStatus("denied"),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: GPS_TTL_MS },
    );
  }, []);

  const setManual = useCallback((cityName) => {
    const c = GHANA_CITIES.find((x) => x.name === cityName);
    if (!c) return;
    const g = { lat: c.lat, lng: c.lng, city: c.name, source: "manual", ts: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(g));
    setGeo(g); setStatus("manual");
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(KEY);
    setGeo(null); setStatus("idle");
  }, []);

  useEffect(() => {
    if (auto && !geo) detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { geo, status, detect, setManual, clear };
}
